import { useToast } from '@hooks/useToast';
import type { Setting, SettingPayload, SettingUpdatePayload } from '@models/setting';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSetting, deleteSetting, updateSetting } from './api';
import { useNavigate } from 'react-router';

function extractApiErrorMessage(error: unknown, defaultMsg: string) {
  let errorMsg = defaultMsg;
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  ) {
    const response = (error as { response?: { data?: { message?: unknown } } }).response;
    if (response?.data?.message) {
      if (Array.isArray(response.data.message)) {
        errorMsg += ': ' + response.data.message.join(', ');
      } else {
        errorMsg += ': ' + response.data.message;
      }
    }
  } else if (error instanceof Error) {
    errorMsg += `: ${error.message}`;
  }
  return errorMsg;
}

export function useUpdateSetting() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<
    Setting,
    unknown,
    {
      id: string;
      setting: SettingUpdatePayload;
    }
  >({
    mutationFn: async ({ id, setting }) => updateSetting(id, setting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Setting updated successfully');
      navigate('/dashboard/settings', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(extractApiErrorMessage(error, 'Failed to update setting'));
    },
  });
}

export function useCreateSetting() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<Setting, unknown, SettingPayload>({
    mutationFn: async (payload) => createSetting(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Setting created successfully');
      navigate('/dashboard/settings', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(extractApiErrorMessage(error, 'Failed to create setting'));
    },
  });
}

export function useDeleteSetting() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await deleteSetting(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Setting deleted successfully');
      navigate('/dashboard/settings', { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(extractApiErrorMessage(error, 'Failed to delete setting'));
    },
  });
}
